import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Loader, Currency, Field } from "@/components/Atoms";
import {
  Shield, Users, Crown, Coins, Settings, Wrench, Ban, ScrollText, Map as MapIcon,
  UserCog, Layers, Gift, PackageOpen, Server, Lock, KeyRound, Flame, Calendar,
  Flag, Sparkles, RefreshCw
} from "lucide-react";



export function Markers() {
  const [markers, setMarkers] = useState([]);
  const [form, setForm] = useState({ id: "", category: "fast_travel", name: "", description: "", x: 4096, y: 4096, icon: "" });
  const [importing, setImporting] = useState(false);
  const [webhookCfg, setWebhookCfg] = useState({ configured: false, webhook_url_masked: "" });
  const [webhookInput, setWebhookInput] = useState("");
  const toast = useToast();
  const load = () => api.get("/admin/map/markers").then(({ data }) => setMarkers(data.markers || []));
  const loadWebhook = () =>
    api.get("/admin/map/tower-run-webhook").then(({ data }) => setWebhookCfg(data));
  useEffect(() => { load(); loadWebhook(); }, []);
  const saveWebhook = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/map/tower-run-webhook", { webhook_url: webhookInput });
      toast.success("Webhook saved");
      setWebhookInput("");
      loadWebhook();
    } catch (e) { toast.error(apiError(e)); }
  };
  const clearWebhook = async () => {
    if (!window.confirm("Clear the tower-run webhook?")) return;
    try {
      await api.put("/admin/map/tower-run-webhook", { webhook_url: "" });
      toast.success("Cleared");
      loadWebhook();
    } catch (e) { toast.error(apiError(e)); }
  };
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/map/markers", { ...form, x: parseFloat(form.x), y: parseFloat(form.y) });
      toast.success("Saved"); setForm({ id: "", category: "fast_travel", name: "", description: "", x: 4096, y: 4096, icon: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { if (!window.confirm(`Delete ${id}?`)) return; await api.delete(`/admin/map/markers/${id}`); load(); };
  const importJson = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const txt = await file.text();
      const json = JSON.parse(txt);
      const { data } = await api.post("/admin/map/import", json);
      toast.success(`Inserted ${data.inserted}, skipped ${data.skipped}`);
      load();
    } catch (e) { toast.error(e?.message || apiError(e)); }
    finally { setImporting(false); e.target.value = ""; }
  };
  return (
    <div className="space-y-6 fade-up">
      {/* Tower-run Discord webhook config */}
      <form onSubmit={saveWebhook} className="glass-card p-5 space-y-3" data-testid="tower-run-webhook-form">
        <h3 className="label">Tower Run — Discord Webhook</h3>
        <p className="text-xs text-[var(--muted)] -mt-1">
          When a player clicks <strong>⚔️ Call for Tower Run</strong> on a boss-tower marker, the site posts a rich embed to this webhook. Create one in <em>Discord → Server Settings → Integrations → Webhooks</em> for the channel you want callouts in.
        </p>
        <div className="text-xs">
          Status:{" "}
          {webhookCfg.configured ? (
            <span className="text-[var(--teal)]">✓ Configured — <code className="text-[10px]">{webhookCfg.webhook_url_masked}</code></span>
          ) : (
            <span className="text-[var(--muted)]">Not configured</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            className="input flex-1 min-w-[220px]"
            type="url"
            placeholder="https://discord.com/api/webhooks/123/abc…"
            value={webhookInput}
            onChange={(e) => setWebhookInput(e.target.value)}
            data-testid="tower-run-webhook-input"
          />
          <button className="btn" disabled={!webhookInput} data-testid="tower-run-webhook-save">Save</button>
          {webhookCfg.configured && (
            <button type="button" onClick={clearWebhook} className="btn btn-secondary btn-danger" data-testid="tower-run-webhook-clear">Clear</button>
          )}
        </div>
      </form>

      <form onSubmit={save} className="glass-card p-5 space-y-3" data-testid="marker-form">
        <h3 className="label">Add Marker</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="ID (snake_case)"><input className="input" required value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} /></Field>
          <Field label="Category">
            <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {["fast_travel","dungeon","alpha_pal","lifmunk_effigy","sealed_realm","boss_tower","treasure","oil_rig","npc","merchant","custom"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Name"><input className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Icon"><input className="input" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} /></Field>
          <Field label="X (0-8192)"><input className="input" type="number" required value={form.x} onChange={(e) => setForm({ ...form, x: e.target.value })} /></Field>
          <Field label="Y (0-8192)"><input className="input" type="number" required value={form.y} onChange={(e) => setForm({ ...form, y: e.target.value })} /></Field>
        </div>
        <Field label="Description"><textarea className="input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
        <div className="flex flex-wrap gap-2 items-center">
          <button className="btn" data-testid="marker-save-button">Save</button>
          <label className="btn btn-secondary cursor-pointer" data-testid="marker-import-label">
            {importing ? "Importing…" : "Bulk Import JSON"}
            <input type="file" accept="application/json" onChange={importJson} className="hidden" data-testid="marker-import-input" />
          </label>
        </div>
      </form>
      <div className="glass-card p-5">
        <h3 className="label mb-3">All Markers ({markers.length})</h3>
        <div className="max-h-96 overflow-y-auto overflow-x-auto">
          <table className="w-full min-w-[540px] text-sm">
            <thead className="text-[10px] uppercase text-[var(--muted)]"><tr><th className="text-left p-2">ID</th><th className="text-left p-2">Cat</th><th className="text-left p-2">Name</th><th className="text-left p-2">X,Y</th><th></th></tr></thead>
            <tbody>
              {markers.map((m) => (
                <tr key={m.id} className="border-t border-white/5">
                  <td className="p-2 font-mono text-[11px]">{m.id}</td>
                  <td className="p-2"><span className="badge text-[var(--muted)] border-white/10">{m.category}</span></td>
                  <td className="p-2">{m.name}</td>
                  <td className="p-2 text-[var(--muted)]">{Math.round(m.x)},{Math.round(m.y)}</td>
                  <td className="p-2 text-right"><button onClick={() => del(m.id)} className="btn btn-sm btn-danger">Del</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Phase C — VIP Renewals, Daily/VIP Streaks, Welcome Reset, Schedule,
// Player Reports, Fusion Config
// ===========================================================================
